import {
  downloadPdfBlob,
  renderPurchaseOrderPdfToBlob,
} from "@/components/manufacturing/pdf/generatePurchaseOrderPdf";
import { ISSUED_PO_FILENAME } from "@/lib/manufacturing/issuedPurchaseOrderFile";
import { purchaseOrderContentLocked } from "@/lib/manufacturing/purchaseOrderTypes";
import {
  fetchStoredIssuedPurchaseOrderPdf,
  issueManufacturingPurchaseOrder,
  uploadPurchaseOrderAttachment,
  type MfgPurchaseOrder,
} from "@/lib/manufacturing/purchaseOrderClient";
import { clearPurchaseOrderOfflineDraft } from "@/lib/manufacturing/purchaseOrderOfflineDraft";

type Msg = { text: string; ok: boolean };

/** Rewrite the stored issued PDF from the current order. Newest file is the one later downloads use. */
export async function refreshStoredIssuedPurchaseOrderPdf(input: {
  order: MfgPurchaseOrder;
  plantName: string | undefined;
}): Promise<{ ok: true; blob: Blob } | { ok: false; error: string; blob?: Blob }> {
  const rendered = await renderPurchaseOrderPdfToBlob({
    order: input.order,
    lines: input.order.lines ?? [],
    plantName: input.order.plant_name || input.plantName,
  });
  if (!rendered.ok) return rendered;
  const file = new File([rendered.blob], ISSUED_PO_FILENAME, { type: "application/pdf" });
  const stored = await uploadPurchaseOrderAttachment({
    po_id: input.order.id,
    kind: "other",
    file,
  });
  if (!stored.ok) return { ok: false, error: stored.error, blob: rendered.blob };
  return { ok: true, blob: rendered.blob };
}

/** Issue a draft if needed, then download the PDF captured at issue. */
export async function issuePurchaseOrderAndDownloadPdf(input: {
  active: MfgPurchaseOrder | null;
  saveDraft: () => Promise<MfgPurchaseOrder | null>;
  confirmation: string;
  ccEmails: string[];
  expectedDeliveryOn: string;
  plantName: string | undefined;
  orgId: string;
  setBusy: (busy: boolean) => void;
  setActive: (order: MfgPurchaseOrder) => void;
  setCcEmails: (emails: string[]) => void;
  setMsg: (msg: Msg) => void;
  loadLists: () => void;
}): Promise<void> {
  let order = input.active;
  const locked = Boolean(order && purchaseOrderContentLocked(order));
  if (!locked) {
    order = await input.saveDraft();
    if (!order) return;
  }
  if (!order) return;

  let issuedOrder = order;
  if (issuedOrder.status === "draft") {
    input.setBusy(true);
    const issued = await issueManufacturingPurchaseOrder({
      po_id: issuedOrder.id,
      expected_delivery_on: input.expectedDeliveryOn || null,
    });
    input.setBusy(false);
    if (!issued.ok) {
      input.setMsg({ text: issued.error, ok: false });
      return;
    }
    issuedOrder = {
      ...issued.data.order,
      cc_emails: issued.data.order.cc_emails?.length ? issued.data.order.cc_emails : issuedOrder.cc_emails,
      lines: issued.data.order.lines ?? issuedOrder.lines,
    };
    input.setActive(issuedOrder);
  } else if (purchaseOrderContentLocked(issuedOrder)) {
    const stored = await fetchStoredIssuedPurchaseOrderPdf(issuedOrder.id);
    if (stored.ok) {
      downloadPdfBlob(stored.blob, issuedOrder.po_number || "draft");
      if (input.orgId) clearPurchaseOrderOfflineDraft(input.orgId);
      input.setMsg({ text: `PO ${issuedOrder.po_number} PDF ready.`, ok: true });
      input.loadLists();
      return;
    }
    if (stored.status !== 404) {
      input.setMsg({ text: stored.error, ok: false });
      return;
    }
  }

  const rendered = await refreshStoredIssuedPurchaseOrderPdf({
    order: issuedOrder,
    plantName: input.plantName,
  });
  if (rendered.blob) downloadPdfBlob(rendered.blob, issuedOrder.po_number || "draft");
  if (!rendered.ok) {
    input.setMsg({
      text: rendered.blob
        ? `PO ${issuedOrder.po_number} downloaded, but it was not stored for the next download. ${rendered.error}`
        : rendered.error,
      ok: false,
    });
    return;
  }
  if (input.orgId) clearPurchaseOrderOfflineDraft(input.orgId);
  input.setMsg({ text: `PO ${issuedOrder.po_number} PDF ready.`, ok: true });
  input.loadLists();
}
